import React, { useState, useEffect, useRef } from "react";
import { IconX, IconLayersIntersect, IconCheck } from "@tabler/icons-react";
import NewSelectInput from "../../../components/common/FilterInputField";
import NewinputField from "../../../components/NewInput";
import ButtonStyled from "../../../components/common/ButtonStyled";
import {
  useFetchProductManifestData,
  useFetchProductManifestViewData,
  useProductManifestAddInventoryMutation,
} from "../../../apis/productManifestApis";
import { useVerifySkuMutation, useFetchAllProductItems } from "../../../apis/productApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useLocationBarcodeVerifyMutation } from "../../../hooks/inventoryHooks";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

interface InteractiveShelvingOperatorPortalProps {
  onClose: () => void;
  warehouseId?: string;
  onSuccess?: () => void;
}

interface PortalRow {
  quantity: string;
  targetLocation: string;
  selectedLocationId: string | null;
  selectedLocationWarehouseId: string | null;
  selectedLocationType: string;
  capacityWarning: string;
  suggestions: any[];
  palletSuggestions: any[];
  showSuggestions: boolean;
}

const InteractiveShelvingOperatorPortal: React.FC<InteractiveShelvingOperatorPortalProps> = ({
  onClose,
  warehouseId,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [inventoryMode, setInventoryMode] = useState<"manifest" | "direct">("manifest");
  const [sellerId, setSellerId] = useState("");
  const [manifest, setManifest] = useState("");
  const [sku, setSku] = useState("");
  const [type, setType] = useState("");
  const [storageMeta, setStorageMeta] = useState({
    binCap: 9999,
    palletCap: 9999,
    supportsBins: true,
    supportsPallets: true,
  });

  const [updatingRowIdx, setUpdatingRowIdx] = useState<number | null>(null);

  const [rows, setRows] = useState<PortalRow[]>([
    {
      quantity: "1",
      targetLocation: "",
      selectedLocationId: null,
      selectedLocationWarehouseId: null,
      selectedLocationType: "",
      capacityWarning: "",
      suggestions: [],
      palletSuggestions: [],
      showSuggestions: false,
    }
  ]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: manifestListResponse } = useFetchProductManifestData(1, 100, "");
  const manifests = manifestListResponse?.productManifests || [];

  const { data: manifestDetails } = useFetchProductManifestViewData(manifest);
  const { data: allProductItems } = useFetchAllProductItems();
  const { data: sellersList } = useMerchantAllListData();

  const { mutate: verifySkuBarcode } = useVerifySkuMutation();
  const { mutate: verifyLocationBarcode } = useLocationBarcodeVerifyMutation();
  
  const targetManifestId = inventoryMode === "direct" ? "direct" : manifest;
  const { mutate: addInventory, isPending: isAddingInventory } = useProductManifestAddInventoryMutation(targetManifestId);

  const manifestOptions = manifests
    ?.filter(
      (m: any) =>
        (m.status === "ASSIGN_TO_INVENTORY_STAFF" ||
          m.status === "ADD_INVENTORY" ||
          m.status === "IN_PROCESS") &&
        (!warehouseId || m.warehouse === warehouseId)
    )
    ?.map((m: any) => ({
      value: m.id,
      text: m.manifestShortId ? `${m.manifestShortId} (${m.clientName || ""})` : m.id,
    })) || [];

  const skuOptions = manifestDetails?.products?.map((p: any) => ({
    value: p.sku,
    text: p.productName ? `${p.sku} - ${p.productName}` : p.sku,
  })) || [];

  const directSkuOptions = allProductItems?.map((p: any) => ({
    value: p.sku,
    text: p.itemName ? `${p.sku} - ${p.itemName}` : p.sku,
  })) || [];

  const sellerOptions = sellersList?.map((s: any) => ({
    value: s.id,
    text: s.name,
  })) || [];

  const matchedProduct = inventoryMode === "manifest"
    ? manifestDetails?.products?.find((p: any) => p.sku === sku)
    : allProductItems?.find((p: any) => p.sku === sku);

  const receivedQty = Number(matchedProduct?.receivedQty || 0);
  const receivedQuantity = Number(matchedProduct?.receivedQuantity || 0);
  const reservedQuantity = Number(matchedProduct?.reservedQuantity || 0);
  const remainingQty = Math.max(0, receivedQty - (receivedQuantity + reservedQuantity));

  const totalEnteredQty = rows.reduce((sum, r) => sum + (parseInt(r.quantity) || 0), 0);
  const isOverRemainingLimit = inventoryMode === "manifest" && sku ? totalEnteredQty > remainingQty : false;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setRows(prev => prev.map(r => ({ ...r, showSuggestions: false })));
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFetchSuggestions = (index: number, overrideQty?: number) => {
    const row = rows[index];
    const qtyToUse = overrideQty !== undefined
      ? overrideQty
      : (parseInt(row.quantity) || (inventoryMode === "manifest" ? (type === "RECEIVED" ? remainingQty : 0) : 1));

    if (!sku || !type || qtyToUse <= 0) {
      toast.error("Please select SKU, type and a valid quantity first.");
      return;
    }
    if (isOverRemainingLimit && overrideQty === undefined) {
      toast.error("Total entered quantity exceeds the remaining manifest quantity.");
      return;
    }

    verifySkuBarcode(
      {
        sku,
        location: "",
        qty: qtyToUse,
        inventoryType: type.toLowerCase(),
        warehouseId: warehouseId || undefined,
        excludeLocationIds: [],
        expiryDate: matchedProduct?.expiryDate || undefined,
      },
      {
        onSuccess: (res: any) => {
          let storageArray = [];
          if (res?.storage) {
            if (typeof res.storage === "string") {
              try {
                storageArray = [JSON.parse(res.storage)];
              } catch (e) {
                console.error("Failed to parse storage string:", e);
              }
            } else if (Array.isArray(res.storage)) {
              storageArray = res.storage;
            } else if (typeof res.storage === "object") {
              storageArray = [res.storage];
            }
          }

          const binStorage = storageArray?.find(
            (s: any) => s?.type === "BINS" || s?.storageType === "BINS"
          );
          const palletStorage = storageArray?.find(
            (s: any) => s?.type === "PALLETS" || s?.storageType === "PALLETS"
          );

          setStorageMeta({
            binCap: binStorage ? parseInt(binStorage.capacity) || 0 : 9999,
            palletCap: palletStorage ? parseInt(palletStorage.capacity) || 0 : 9999,
            supportsBins: !!binStorage,
            supportsPallets: !!palletStorage,
          });

          const binSuggestions = res?.suggestedBins || [];
          const palletSuggestionsList = res?.suggestedPallets || [];

          setRows(prev => prev.map((r, idx) => {
            if (idx === index) {
              return {
                ...r,
                suggestions: binSuggestions,
                palletSuggestions: palletSuggestionsList,
                showSuggestions: true,
              };
            }
            return r;
          }));
          toast.success("Suggestions loaded");
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to load suggestions");
        },
      }
    );
  };

  const handleModeChange = (mode: "manifest" | "direct") => {
    setInventoryMode(mode);
    setManifest("");
    setSku("");
    setType("");
    setSellerId("");
    setRows([
      {
        quantity: "1",
        targetLocation: "",
        selectedLocationId: null,
        selectedLocationWarehouseId: null,
        selectedLocationType: "",
        capacityWarning: "",
        suggestions: [],
        palletSuggestions: [],
        showSuggestions: false,
      }
    ]);
  };

  const handleManifestChange = (val: string) => {
    setManifest(val);
    setSku("");
    setRows([
      {
        quantity: "1",
        targetLocation: "",
        selectedLocationId: null,
        selectedLocationWarehouseId: null,
        selectedLocationType: "",
        capacityWarning: "",
        suggestions: [],
        palletSuggestions: [],
        showSuggestions: false,
      }
    ]);
  };

  const handleSkuChange = (val: string) => {
    setSku(val);
    setRows([
      {
        quantity: "1",
        targetLocation: "",
        selectedLocationId: null,
        selectedLocationWarehouseId: null,
        selectedLocationType: "",
        capacityWarning: "",
        suggestions: [],
        palletSuggestions: [],
        showSuggestions: false,
      }
    ]);
  };

  const handleQuantityChange = (val: string, index: number) => {
    setRows(prev => prev.map((row, idx) => {
      if (idx === index) {
        return {
          ...row,
          quantity: val,
          capacityWarning: "",
        };
      }
      return row;
    }));
  };

  const handleTypeChange = (val: string) => {
    setType(val);
    setRows([
      {
        quantity: "1",
        targetLocation: "",
        selectedLocationId: null,
        selectedLocationWarehouseId: null,
        selectedLocationType: "",
        capacityWarning: "",
        suggestions: [],
        palletSuggestions: [],
        showSuggestions: false,
      }
    ]);
  };

  const handleLocationChange = (val: string, index: number) => {
    setRows(prev => prev.map((row, idx) => {
      if (idx === index) {
        return {
          ...row,
          targetLocation: val,
          selectedLocationId: null,
          selectedLocationWarehouseId: null,
          selectedLocationType: "",
          capacityWarning: "",
        };
      }
      return row;
    }));
  };

  const handleSelectSuggestion = (loc: any, locType: "BINS" | "PALLETS", index: number) => {
    const unitCap = locType === "PALLETS" ? storageMeta.palletCap : storageMeta.binCap;
    const qty = parseInt(rows[index].quantity) || 0;
    const freeSpace = unitCap - loc.currentInventoryCount;
    const warning = (unitCap > 0 && qty > freeSpace)
      ? `Only ${Math.max(freeSpace, 0)} space available at "${loc.barcodeLabel}".`
      : "";

    setRows(prev => prev.map((row, idx) => {
      if (idx === index) {
        return {
          ...row,
          targetLocation: loc.barcodeLabel,
          selectedLocationId: loc.id,
          selectedLocationWarehouseId: loc.warehouseId,
          selectedLocationType: locType,
          capacityWarning: warning,
          showSuggestions: false,
        };
      }
      return row;
    }));

    if (!warning) {
      toast.success("Location selected");
    }
  };

  const handleVerifyLocation = (barcode: string, index: number) => {
    if (!barcode) return;
    verifyLocationBarcode(
      {
        barcodeId: barcode,
        inventoryType: type.toLowerCase(),
        expiryDate: matchedProduct?.expiryDate || null,
        warehouseId: warehouseId || undefined,
      } as any,
      {
        onSuccess: (res: any) => {
          const barcodeLabel = res?.barcodeLabel || barcode;
          const availableQty = parseInt(res?.availableQty ?? "0");
          const locType = res?.locationType || "";

          const unitCap = locType === "PALLETS" ? storageMeta.palletCap : storageMeta.binCap;
          const qty = parseInt(rows[index].quantity) || 0;
          const freeSpace = unitCap - availableQty;
          const warning = (unitCap > 0 && qty > freeSpace)
            ? `Only ${Math.max(freeSpace, 0)} space available at "${barcodeLabel}".`
            : "";

          setRows(prev => prev.map((row, idx) => {
            if (idx === index) {
              return {
                ...row,
                targetLocation: barcodeLabel,
                selectedLocationId: res?.id || null,
                selectedLocationWarehouseId: res?.warehouseId || null,
                selectedLocationType: locType,
                capacityWarning: warning,
              };
            }
            return row;
          }));

          if (warning) {
            toast.error(warning);
          } else {
            toast.success("Location verified");
          }
        },
        onError: (err: any) => {
          toast.error(err?.message || String(err) || "Location verification failed");
          setRows(prev => prev.map((row, idx) => {
            if (idx === index) {
              return {
                ...row,
                targetLocation: "",
                selectedLocationId: null,
                selectedLocationWarehouseId: null,
                selectedLocationType: "",
                capacityWarning: "",
              };
            }
            return row;
          }));
        }
      }
    );
  };

  const handleAutoDistribute = (index: number) => {
    const row = rows[index];
    const qty = parseInt(row.quantity) || 0;
    if (qty <= 0) return;

    const isPalletMode = row.selectedLocationType === "PALLETS" || (!storageMeta.supportsBins && storageMeta.supportsPallets);
    const availableTargets = isPalletMode ? row.palletSuggestions : row.suggestions;
    const unitCap = isPalletMode ? storageMeta.palletCap : storageMeta.binCap;

    if (availableTargets.length === 0) {
      toast.error("No suggestions available for distribution.");
      return;
    }

    let remaining = qty;
    const updatedRows = [...rows];
    const newSplits: PortalRow[] = [];

    if (row.targetLocation) {
      const currentLocSuggestion = availableTargets.find(t => t.barcodeLabel === row.targetLocation);
      const currentLocInventory = currentLocSuggestion ? currentLocSuggestion.currentInventoryCount : 0;
      const freeSpaceInCurrent = Math.max(0, unitCap - currentLocInventory);
      if (freeSpaceInCurrent > 0) {
        const take = Math.min(remaining, freeSpaceInCurrent);
        updatedRows[index] = {
          ...row,
          quantity: String(take),
          selectedLocationId: currentLocSuggestion ? currentLocSuggestion.id : row.selectedLocationId,
          selectedLocationWarehouseId: currentLocSuggestion ? currentLocSuggestion.warehouseId : row.selectedLocationWarehouseId,
          selectedLocationType: isPalletMode ? "PALLETS" : "BINS",
          capacityWarning: "",
        };
        remaining -= take;
      }
    }

    let targetIdx = 0;
    while (remaining > 0 && targetIdx < availableTargets.length) {
      const target = availableTargets[targetIdx];
      const locationAlreadyUsed = updatedRows.some(r => r.targetLocation === target.barcodeLabel) || newSplits.some(s => s.targetLocation === target.barcodeLabel);
      if (locationAlreadyUsed) {
        targetIdx++;
        continue;
      }

      const targetFreeSpace = Math.max(0, unitCap - (target.currentInventoryCount || 0));
      if (targetFreeSpace > 0) {
        const take = Math.min(remaining, targetFreeSpace);
        newSplits.push({
          quantity: String(take),
          targetLocation: target.barcodeLabel,
          selectedLocationId: target.id,
          selectedLocationWarehouseId: target.warehouseId,
          selectedLocationType: isPalletMode ? "PALLETS" : "BINS",
          capacityWarning: "",
          suggestions: [],
          palletSuggestions: [],
          showSuggestions: false,
        });
        remaining -= take;
      }
      targetIdx++;
    }

    if (remaining > 0) {
      if (newSplits.length > 0) {
        newSplits[newSplits.length - 1].quantity = String(parseInt(newSplits[newSplits.length - 1].quantity) + remaining);
      } else {
        updatedRows[index].quantity = String(parseInt(updatedRows[index].quantity) + remaining);
      }
    }

    setRows([...updatedRows, ...newSplits]);
    toast.success("Quantity distributed into input rows.");
  };

  const handleRemoveRow = (index: number) => {
    setRows(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleUpdateRow = (index: number) => {
    if (inventoryMode === "manifest" && !manifest) {
      toast.error("Please select a manifest.");
      return;
    }
    if (inventoryMode === "direct" && !sellerId) {
      toast.error("Please select a seller.");
      return;
    }
    if (!sku) {
      toast.error("Please select an item SKU.");
      return;
    }
    const row = rows[index];
    if (!row.targetLocation) {
      toast.error("The row must have a target location.");
      return;
    }
    const qtyVal = parseInt(row.quantity) || 0;
    if (qtyVal <= 0) {
      toast.error("Row quantity must be greater than zero.");
      return;
    }

    setUpdatingRowIdx(index);

    verifyLocationBarcode(
      {
        barcodeId: row.targetLocation,
        inventoryType: type.toLowerCase(),
        expiryDate: matchedProduct?.expiryDate || null,
        warehouseId: warehouseId || undefined,
      } as any,
      {
        onSuccess: (res: any) => {
          const barcodeLabel = res?.barcodeLabel || row.targetLocation;
          const availableQty = parseInt(res?.availableQty ?? "0");
          const locType = res?.locationType || "";

          const unitCap = locType === "PALLETS" ? storageMeta.palletCap : storageMeta.binCap;
          const freeSpace = unitCap - availableQty;

          if (unitCap > 0 && qtyVal > freeSpace) {
            const warningMsg = `Only ${Math.max(freeSpace, 0)} space available at "${barcodeLabel}".`;
            toast.error(warningMsg);
            setRows(prev => prev.map((r, rIdx) => {
              if (rIdx === index) {
                return {
                  ...r,
                  selectedLocationId: res?.id || null,
                  selectedLocationWarehouseId: res?.warehouseId || null,
                  selectedLocationType: locType,
                  capacityWarning: warningMsg,
                };
              }
              return r;
            }));
            setUpdatingRowIdx(null);
            return;
          }

          let finalLocationId = res?.id || row.selectedLocationId;
          let finalWarehouseId = res?.warehouseId || row.selectedLocationWarehouseId;

          if (!finalLocationId) {
            const suggestionsList = locType === "PALLETS" ? row.palletSuggestions : row.suggestions;
            const matchedSuggest = suggestionsList.find((t: any) => t.barcodeLabel === barcodeLabel);
            if (matchedSuggest) {
              finalLocationId = matchedSuggest.id;
              finalWarehouseId = matchedSuggest.warehouseId;
            }
          }

          const payloadProduct = {
            productId: matchedProduct?.productId || matchedProduct?.id,
            scannedLocation: barcodeLabel,
            sku: sku,
            quantity: qtyVal,
            expiryDate: matchedProduct?.expiryDate || null,
            hasExpiry: !!matchedProduct?.expiryDate,
            productName: matchedProduct?.productName || matchedProduct?.itemName || "",
            inventoryType: type.toLowerCase() as "received" | "reserved",
            scannedLocationId: finalLocationId || null,
            scannedLocationWarehouseId: finalWarehouseId || null,
          };

          const payload = inventoryMode === "direct"
            ? { clientId: sellerId, products: [payloadProduct] }
            : { products: [payloadProduct] };

          addInventory(
            payload,
            {
              onSuccess: () => {
                toast.success("Inventory successfully added!");
                setUpdatingRowIdx(null);

                if (rows.length === 1) {
                  setRows([
                    {
                      quantity: "1",
                      targetLocation: "",
                      selectedLocationId: null,
                      selectedLocationWarehouseId: null,
                      selectedLocationType: "",
                      capacityWarning: "",
                      suggestions: [],
                      palletSuggestions: [],
                      showSuggestions: false,
                    }
                  ]);
                } else {
                  setRows(prev => prev.filter((_, idx) => idx !== index));
                }

                // Invalidate React Query caches to trigger refresh of manifest details and lists
                if (inventoryMode === "manifest") {
                  queryClient.invalidateQueries({ queryKey: ["itemProductViewData", manifest] });
                }
                queryClient.invalidateQueries({ queryKey: ["itemProductData"] });
                queryClient.invalidateQueries({ queryKey: ["itemInventoriesData"] });
                queryClient.invalidateQueries({ queryKey: ["productItemsMovementData"] });

                if (onSuccess) {
                  onSuccess();
                }
              },
              onError: (err: any) => {
                toast.error(err?.message || String(err) || "Failed to add inventory");
                setUpdatingRowIdx(null);
              }
            }
          );
        },
        onError: (err: any) => {
          toast.error(err?.message || String(err) || "Location verification failed");
          setUpdatingRowIdx(null);
        }
      }
    );
  };

  return (
    <div className="bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">
          Interactive Shelving Operator Portal
        </h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full transition text-slate-500 hover:text-slate-700"
        >
          <IconX size={18} />
        </button>
      </div>
      <p className="text-xs text-[#212121]/70 mb-3">
        Choose Manifest, SKU, Type, Quantity, and Target Location.
      </p>

      <div className="flex bg-slate-100 p-1 rounded-xl mb-2 gap-1">
        <button
          type="button"
          onClick={() => handleModeChange("manifest")}
          className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${inventoryMode === "manifest" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          Manifest Inventory
        </button>
        <button
          type="button"
          onClick={() => handleModeChange("direct")}
          className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${inventoryMode === "direct" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          Direct Inventory
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {inventoryMode === "manifest" ? (
          <>
            <NewSelectInput
              name="manifest"
              label="Select Manifest"
              placeholder="Select Manifest"
              selectOptions={manifestOptions}
              value={manifest}
              onChange={handleManifestChange}
            />
            <NewSelectInput
              name="sku"
              label="Select Item SKU"
              placeholder={manifest ? "Select Item SKU" : "Select Manifest first"}
              selectOptions={skuOptions}
              value={sku}
              onChange={handleSkuChange}
              disabled={!manifest}
            />
            <NewSelectInput
              name="type"
              label="Type"
              placeholder={sku ? "Select Type" : "Select SKU first"}
              selectOptions={[
                { value: "RECEIVED", text: "Received" },
                { value: "RESERVED", text: "Reserved" },
              ]}
              value={type}
              onChange={handleTypeChange}
              disabled={!sku}
            />
          </>
        ) : (
          <>
            <NewSelectInput
              name="sku"
              label="Select Item SKU"
              placeholder="Select Item SKU"
              selectOptions={directSkuOptions}
              value={sku}
              onChange={handleSkuChange}
            />
            <NewSelectInput
              name="seller"
              label="Select Seller"
              placeholder={sku ? "Select Seller" : "Select SKU first"}
              selectOptions={sellerOptions}
              value={sellerId}
              onChange={(val) => setSellerId(val)}
              disabled={!sku}
            />
            <NewSelectInput
              name="type"
              label="Type"
              placeholder={sellerId ? "Select Type" : "Select Seller first"}
              selectOptions={[
                { value: "RECEIVED", text: "Received" },
                { value: "RESERVED", text: "Reserved" },
              ]}
              value={type}
              onChange={handleTypeChange}
              disabled={!sellerId}
            />
          </>
        )}

        {inventoryMode === "manifest" && sku && matchedProduct && (
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl p-3 flex justify-between my-1">
            <span>Inbound Qty: <strong className="text-slate-900">{receivedQty}</strong></span>
            <span>Already Shelved: <strong className="text-slate-900">{receivedQuantity + reservedQuantity}</strong></span>
            <span>Remaining: <strong className="text-blue-600 font-bold">{remainingQty}</strong></span>
          </div>
        )}

        {isOverRemainingLimit && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl p-3 font-semibold my-1">
            Total entered quantity ({totalEnteredQty}) exceeds the remaining manifest quantity ({remainingQty}).
          </div>
        )}

        {type && (
          <div className="flex flex-col gap-3" ref={dropdownRef}>
          {rows.map((row, idx) => (
            <div key={idx} className="space-y-2">
              <div className="grid grid-cols-[1fr_1fr_80px] gap-2 items-end">
                {type === "RECEIVED" ? (
                  <>
                    <div className="relative">
                      <NewinputField
                        name={`targetLocation-${idx}`}
                        title={idx === 0 ? "Target Location" : ""}
                        type="text"
                        placeholder="Enter Location"
                        value={row.targetLocation}
                        disabled={!sku || !type}
                        other={{
                          onChange: (e: any) => handleLocationChange(e.target.value, idx),
                          onKeyDown: (e: any) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleVerifyLocation(e.currentTarget.value, idx);
                            }
                          },
                          onClick: () => {
                            if (!sku || !type || isOverRemainingLimit) return;
                            if (row.suggestions.length > 0 || row.palletSuggestions.length > 0) {
                              setRows(prev => prev.map((r, rIdx) => rIdx === idx ? { ...r, showSuggestions: true } : r));
                            } else {
                              handleFetchSuggestions(idx, inventoryMode === "manifest" ? remainingQty : undefined);
                            }
                          }
                        }}
                        inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                      />

                      {row.showSuggestions && !isOverRemainingLimit && (row.suggestions.length > 0 || row.palletSuggestions.length > 0) && (
                        <div className="absolute left-0 top-full w-full max-h-60 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-[0_12px_40px_-10px_rgba(0,0,0,0.2)] z-[110] py-0 custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200">
                          <div className="sticky top-0 bg-white px-4 py-2 flex items-center justify-between border-b border-gray-50 mb-1 z-10">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              Suggested Locations
                            </span>
                            <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-md font-bold">
                              {row.suggestions.length + row.palletSuggestions.length} found
                            </span>
                          </div>

                          {row.suggestions.length > 0 && (
                            <>
                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Bins</div>
                              {row.suggestions.map((loc: any) => (
                                <button
                                  key={loc.barcodeLabel}
                                  type="button"
                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelectSuggestion(loc, "BINS", idx);
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                      {loc.barcodeLabel}
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                      {loc.currentInventoryCount} items stored
                                      <span className="text-blue-600 font-semibold ml-1">
                                        ({Math.max(0, storageMeta.binCap - loc.currentInventoryCount)} left)
                                      </span>
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </>
                          )}

                          {row.palletSuggestions.length > 0 && (
                            <>
                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Pallets</div>
                              {row.palletSuggestions.map((loc: any) => (
                                <button
                                  key={loc.barcodeLabel}
                                  type="button"
                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelectSuggestion(loc, "PALLETS", idx);
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                      {loc.barcodeLabel}
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                      {loc.currentInventoryCount} items stored
                                      <span className="text-blue-600 font-semibold ml-1">
                                        ({Math.max(0, storageMeta.palletCap - loc.currentInventoryCount)} left)
                                      </span>
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <NewinputField
                      name={`quantity-${idx}`}
                      title={idx === 0 ? "Intake Quantity" : ""}
                      type="number"
                      min="1"
                      placeholder="Qty"
                      value={row.quantity}
                      disabled={!sku || !type || !row.targetLocation}
                      other={{
                        onChange: (e: any) => {
                          const val = e.target.value;
                          if (val === "" || Number(val) > 0) {
                            handleQuantityChange(val, idx);
                          }
                        },
                        onKeyDown: (e: any) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleFetchSuggestions(idx);
                          }
                        }
                      }}
                      inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                    />
                  </>
                ) : (
                  <>
                    <NewinputField
                      name={`quantity-${idx}`}
                      title={idx === 0 ? "Intake Quantity" : ""}
                      type="number"
                      min="1"
                      placeholder="Qty"
                      value={row.quantity}
                      disabled={!sku || !type}
                      other={{
                        onChange: (e: any) => {
                          const val = e.target.value;
                          if (val === "" || Number(val) > 0) {
                            handleQuantityChange(val, idx);
                          }
                        },
                        onKeyDown: (e: any) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleFetchSuggestions(idx);
                          }
                        }
                      }}
                      inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                    />

                    <div className="relative">
                      <NewinputField
                        name={`targetLocation-${idx}`}
                        title={idx === 0 ? "Target Location" : ""}
                        type="text"
                        placeholder="Enter Location"
                        value={row.targetLocation}
                        disabled={!sku || !type || !row.quantity || parseInt(row.quantity) <= 0}
                        other={{
                          onChange: (e: any) => handleLocationChange(e.target.value, idx),
                          onKeyDown: (e: any) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleVerifyLocation(e.currentTarget.value, idx);
                            }
                          },
                          onClick: () => {
                            if (!sku || !type || !row.quantity || parseInt(row.quantity) <= 0 || isOverRemainingLimit) return;
                            if (row.suggestions.length > 0 || row.palletSuggestions.length > 0) {
                              setRows(prev => prev.map((r, rIdx) => rIdx === idx ? { ...r, showSuggestions: true } : r));
                            }
                          }
                        }}
                        inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                      />

                      {row.showSuggestions && !isOverRemainingLimit && (row.suggestions.length > 0 || row.palletSuggestions.length > 0) && (
                        <div className="absolute left-0 top-full w-full max-h-60 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-[0_12px_40px_-10px_rgba(0,0,0,0.2)] z-[110] py-0 custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200">
                          <div className="sticky top-0 bg-white px-4 py-2 flex items-center justify-between border-b border-gray-50 mb-1 z-10">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              Suggested Locations
                            </span>
                            <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-md font-bold">
                              {row.suggestions.length + row.palletSuggestions.length} found
                            </span>
                          </div>

                          {row.suggestions.length > 0 && (
                            <>
                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Bins</div>
                              {row.suggestions.map((loc: any) => (
                                <button
                                  key={loc.barcodeLabel}
                                  type="button"
                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelectSuggestion(loc, "BINS", idx);
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                      {loc.barcodeLabel}
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                      {loc.currentInventoryCount} items stored
                                      <span className="text-blue-600 font-semibold ml-1">
                                        ({Math.max(0, storageMeta.binCap - loc.currentInventoryCount)} left)
                                      </span>
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </>
                          )}

                          {row.palletSuggestions.length > 0 && (
                            <>
                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Pallets</div>
                              {row.palletSuggestions.map((loc: any) => (
                                <button
                                  key={loc.barcodeLabel}
                                  type="button"
                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelectSuggestion(loc, "PALLETS", idx);
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                      {loc.barcodeLabel}
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                      {loc.currentInventoryCount} items stored
                                      <span className="text-blue-600 font-semibold ml-1">
                                        ({Math.max(0, storageMeta.palletCap - loc.currentInventoryCount)} left)
                                      </span>
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <button
                  type="button"
                  disabled={
                    (inventoryMode === "manifest" ? !manifest : !sellerId) ||
                    !sku ||
                    !row.targetLocation ||
                    !row.quantity ||
                    parseInt(row.quantity) <= 0 ||
                    !!row.capacityWarning ||
                    updatingRowIdx !== null ||
                    isAddingInventory ||
                    isOverRemainingLimit
                  }
                  onClick={() => handleUpdateRow(idx)}
                  className={`w-full py-2 text-xs font-bold rounded-full transition-all mb-1 flex items-center justify-center ${(
                    (inventoryMode === "manifest" ? !manifest : !sellerId) ||
                    !sku ||
                    !row.targetLocation ||
                    !row.quantity ||
                    parseInt(row.quantity) <= 0 ||
                    !!row.capacityWarning ||
                    updatingRowIdx !== null ||
                    isAddingInventory ||
                    isOverRemainingLimit)
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-[#FBD037] hover:bg-yellow-400 text-black shadow-sm"
                    }`}
                >
                  {updatingRowIdx === idx ? (
                    <svg
                      className="animate-spin h-3.5 w-3.5 text-black"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
                      />
                    </svg>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>

              {row.capacityWarning && (
                <div className="mt-1 p-3 bg-red-50/50 border border-red-200 rounded-xl space-y-2">
                  <p className="text-xs text-red-700 font-semibold flex items-center gap-1.5">
                    <span>{row.capacityWarning}</span>
                  </p>

                  <div className="flex flex-col gap-1.5 pt-1.5 border-t border-red-100">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                      Suggested {row.selectedLocationType === "PALLETS" ? "Pallets" : "Bins"}:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(row.selectedLocationType === "PALLETS" ? row.palletSuggestions : row.suggestions)
                        ?.slice(0, 4)
                        .map((loc) => {
                          const cap = row.selectedLocationType === "PALLETS" ? storageMeta.palletCap : storageMeta.binCap;
                          const left = cap - loc.currentInventoryCount;
                          return (
                            <button
                              key={loc.barcodeLabel}
                              type="button"
                              onClick={() => handleSelectSuggestion(loc, row.selectedLocationType === "PALLETS" ? "PALLETS" : "BINS", idx)}
                              className="px-2 py-1 bg-white hover:bg-yellow-50 text-[10px] font-bold rounded border border-gray-200 transition text-gray-700 flex items-center gap-1"
                            >
                              {loc.barcodeLabel}
                              <span className="text-blue-600">({left} left)</span>
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleAutoDistribute(idx)}
                    className="w-full py-2 bg-yellow-400 hover:bg-yellow-500 text-black rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition shadow-sm hover:shadow"
                  >
                    <IconLayersIntersect size={14} /> Auto-Distribute
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        )}

      </div>
    </div>
  );
};

export default InteractiveShelvingOperatorPortal;
